import { useState, useEffect, useCallback } from 'react'
import { resolved, useQuery } from '@hapana/shared/web/services/gqty'
import { useDebounce } from '@hapana/shared/web/hooks/useDebounce'

export interface ArticleResult {
  id: string
  title: string
  snippet: string
  category: string
  slug?: string
}

export function useArticleSearch(searchQuery: string) {
  const query = useQuery({ suspense: false }) as any
  const [results, setResults] = useState<ArticleResult[]>([])
  const [isSearching, setIsSearching] = useState(false)

  const debouncedQuery = useDebounce(searchQuery, 500)

  const performSearch = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        setResults([])
        return
      }

      setIsSearching(true)
      try {
        const data = await resolved(() => {
          const response = query.searchKnowledge({
            input: {
              query: searchTerm,
              corporateId: 'default',
              limit: 5,
            },
          })
          return response.results.map((r: any) => ({
            id: r.document.id,
            title: r.document.title,
            snippet: r.relevantSnippet,
            category: r.document.category,
            slug: r.document.slug,
          }))
        })
        setResults(data || [])
      } catch (error) {
        console.error('[useArticleSearch] Search error:', error)
        setResults([])
      } finally {
        setIsSearching(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- query is a gqty proxy that changes every render
    []
  )

  useEffect(() => {
    performSearch(debouncedQuery)
  }, [debouncedQuery, performSearch])

  return { results, isSearching }
}
